import { handleApiRequest } from 'Utils/handleApiRequest';
import {
  RegisterAssetPayload,
  UpdateAssetPayload,
  DeployAssetPayload,
  MoveAssetPayload,
  CheckOutAssetPayload,
} from './types';

// Run a serialized mutation and hand the caller a discriminated result: the response body
// (`{ data }`) on success — unchanged, so existing `response?.data` checks still work — or
// `{ error: { status, message, errors } }` on failure. Routed through handleApiRequest (with its
// structured-error flag) so all global handling still fires — 401 logout/CSRF recovery, 403/500
// toasts, 422 form errors, fetching cleanup — while the container also gets the backend's 409/422
// message to show inline and the 409 status to refresh an asset before retrying. (204-no-body
// deletes resolve to '' — a string — preserving the existing delete-success quirk.)
const runMutation = (dispatch: any, apiRequest: Promise<any>) =>
  handleApiRequest(dispatch, apiRequest, 'FORM_ERRORS', 'SET_FETCHING', null, true);

// Fetch every page of a paginated list endpoint and return one combined `{ data, meta }`. The
// deploy/move destination picker uses plain <select>s with no search or load-more, so it must hold
// the full list. Reads meta.last_page from page 1, then pages sequentially. Returns null if ANY
// page fails (including page 1) — a partial list would silently hide valid rows, so the caller
// treats the whole load as failed rather than showing an incomplete picker.
const fetchAllPages = async (fetchPage: (page: number) => Promise<any>) => {
  const first = await fetchPage(1);
  if (!first?.data) return null;
  const all = [...first.data];
  const lastPage = first?.meta?.last_page ?? 1;
  for (let page = 2; page <= lastPage; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const next = await fetchPage(page);
    if (!next?.data) return null;
    all.push(...next.data);
  }
  return { data: all, meta: first.meta };
};

// Serialized equipment Gantt data source (WEBAPP-FR-007). Reads the dedicated, paginated,
// name-resolved timeline endpoint — one page = N project rows, each with its bars. Returns the
// raw { data, meta } response (or undefined on failure) for the container to shape.
export const getSerializedEquipmentTimeline =
  (companyId: string, page = 1) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`/companies/${companyId}/equipment-asset-timeline`, { params: { page } })
    );

    return response;
  };

// Company equipment catalog (FR-002 union) — populates the "type" dropdown when registering a
// serialized unit. Returns { data: CatalogItem[] }.
export const listCompanyEquipmentCatalog =
  (companyId: string) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`/companies/${companyId}/equipment`));

    return response;
  };

// The company's serialized asset pool. Paginated (default 25, max 100) and filterable by
// status / catalog_uuid / search (name, serial, asset_tag). Returns { data: EquipmentAsset[], meta }.
export const listCompanyEquipmentAssets =
  (companyId: string, params: Record<string, any> = {}) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`/companies/${companyId}/equipment-assets`, { params })
    );

    return response;
  };

// A single serialized unit (its current updated_at optimistic-lock token). Used to refresh an
// asset after a 409 conflict so the next mutation carries the current token. Returns { data }.
export const getEquipmentAsset =
  (assetId: number) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`/equipment-assets/${assetId}`));

    return response;
  };

// Register a serialized unit into the company pool (MONGOOSE-FR-015). Returns { data: EquipmentAsset }
// on success or { error } on failure (e.g. 422 validation).
export const registerCompanyEquipmentAsset =
  (companyId: string, payload: RegisterAssetPayload) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.post(`/companies/${companyId}/equipment-assets`, payload));

// Edit a serialized unit's identity/financial fields (+ status). Requires updated_at
// (optimistic lock). Returns { data: EquipmentAsset } on success or { error } on 409/422.
export const updateEquipmentAsset =
  (assetId: number, payload: UpdateAssetPayload) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.patch(`/equipment-assets/${assetId}`, payload));

// Retire (soft-delete) a serialized unit. Backend returns 204 (no body), so — per the
// delete-success quirk (see CLAUDE.md) — success is a string response ('' ), not { data }.
// Failure returns { error } so the caller can surface the backend message.
export const retireEquipmentAsset =
  (assetId: number) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.delete(`/equipment-assets/${assetId}`));

// Per-unit placement history (open date_out = still deployed). Returns { data: AssetPlacement[] }.
export const getAssetPlacements =
  (assetId: number) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`/equipment-assets/${assetId}/placements`));

    return response;
  };

// Check a serialized unit into a room (deploy). status -> deployed. Returns { data: AssetPlacement }
// on success or { error } on 409/422.
export const deployAssetToRoom =
  (assetId: number, payload: DeployAssetPayload) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.post(`/equipment-assets/${assetId}/placements`, payload));

// Edit an existing placement's dates (correct when a unit was deployed/removed). Requires the
// placement's updated_at (optimistic lock). Returns { data: AssetPlacement } on success or { error }
// on 409/422.
export const updatePlacement =
  (placementId: number, payload: { date_in?: string; date_out?: string | null; updated_at: string }) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.patch(`/equipment-asset-placements/${placementId}`, payload));

// Delete a historical (closed) placement record. Soft-delete → 204, so success is a string (delete
// quirk); the active placement can't be deleted (backend 422). Returns { error } on failure.
export const deletePlacement =
  (placementId: number) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.delete(`/equipment-asset-placements/${placementId}`));

// Atomically move a deployed unit to another room. Requires updated_at (optimistic lock).
// Returns { data: EquipmentAsset } on success or { error } on 409/422 (409 = stale updated_at).
export const moveAsset =
  (assetId: number, payload: MoveAssetPayload) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.post(`/equipment-assets/${assetId}/move`, payload));

// Check a deployed unit out of its room. status -> available. Requires updated_at (optimistic lock).
// Returns { data: EquipmentAsset } on success or { error } on 409/422 (409 = stale updated_at).
export const checkOutAsset =
  (assetId: number, payload: CheckOutAssetPayload) =>
  async (dispatch: any, _getState: any, utils: any) =>
    runMutation(dispatch, utils.Api.post(`/equipment-assets/${assetId}/check-out`, payload));

// --- Project → Location → Room cascade (deploy/move destination picker) ---

// All active-company projects (every page). Returns { data, meta } (each project: id, uid, alias?,
// address). The projects endpoint has a fixed page size of 15 and ignores per_page, so we page
// through to give the picker the full list.
export const listCompanyProjectsForPicker =
  (companyId: string) =>
  async (dispatch: any, _getState: any, utils: any) =>
    fetchAllPages((page) =>
      handleApiRequest(
        dispatch,
        utils.Api.get(`companies/${companyId}/projects`, {
          params: { 'filter[status]': 1, include: 'address', sort: '-created_at', page },
        })
      )
    );

// A single project including properties — used only to resolve the property id
// (data.properties.slice(-1)[0].id) between the project and location steps.
export const getProjectDetail =
  (projectId: number) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`projects/${projectId}`, { params: { include: 'properties' } })
    );

    return response;
  };

// All locations under a property (every page). Returns { data, meta } (each location: id, name).
// Requests the max page size (30) and pages through so the picker holds the full list.
export const listPropertyLocations =
  (propertyId: number) =>
  async (dispatch: any, _getState: any, utils: any) =>
    fetchAllPages((page) =>
      handleApiRequest(
        dispatch,
        utils.Api.get(`properties/${propertyId}/locations`, { params: { include: 'locationType', limit: 30, page } })
      )
    );

// All rooms under a location (every page). Returns { data, meta } (each room: id, room_type.name).
// Requests the max page size (30) and pages through so the picker holds the full list.
export const listLocationRooms =
  (locationId: number) =>
  async (dispatch: any, _getState: any, utils: any) =>
    fetchAllPages((page) =>
      handleApiRequest(
        dispatch,
        utils.Api.get(`locations/${locationId}/rooms`, { params: { include: 'roomType,level', limit: 30, page } })
      )
    );

// Serialized units currently deployed in a room (open placement). Returns { data: EquipmentAsset[] }.
// Powers the per-room serialized equipment list in the project RocketDry workflow.
export const listRoomEquipmentAssets =
  (roomId: number) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`/rooms/${roomId}/equipment-assets`));

    return response;
  };

// --- Custom equipment TYPE CRUD (company equipment_inventories = the custom catalog entries) ---
// Serialized-local (no legacy reducer) so mode isolation holds; hits the shared catalog endpoints.

// List ALL of the company's custom equipment types (every page). The endpoint is paginated, so we
// page through — otherwise types past the first page can't be edited or deleted. Returns
// { data, meta } (each: id, name), or null if any page fails.
export const listCompanyEquipmentTypes =
  (companyId: string) =>
  async (dispatch: any, _getState: any, utils: any) =>
    fetchAllPages((page) =>
      handleApiRequest(dispatch, utils.Api.get(`/companies/${companyId}/equipment-inventories`, { params: { page } }))
    );

// Create a custom equipment type. Returns { data } on success.
export const createCompanyEquipmentType =
  (companyId: string, name: string) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`/companies/${companyId}/equipment-inventories`, { name })
    );

    return response;
  };

// Rename a custom equipment type. Returns { data } on success.
export const updateCompanyEquipmentType =
  (typeId: number, name: string) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.put(`/equipment-inventories/${typeId}`, { name }));

    return response;
  };

// Delete a custom equipment type. Backend returns 204 (no body) → success is a string (delete quirk).
export const deleteCompanyEquipmentType =
  (typeId: number) =>
  async (dispatch: any, _getState: any, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.delete(`/equipment-inventories/${typeId}`));

    return response;
  };
